import { useCallback, useContext, useMemo, useState } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import { Settings } from '@atlaskit/atlassian-navigation';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import ActivityIcon from '@atlaskit/icon/glyph/activity';
import AtlassianIcon from '@atlaskit/icon/glyph/emoji/atlassian';
import LabsIcon from '@atlaskit/icon/glyph/jira/labs';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import PersonCircleIcon from '@atlaskit/icon/glyph/person-circle';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { LinkItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { MenuItem } from '../../types';
import urls from '../../urls';

import {
  HorizontalNavContext,
  HorizontalNavProps,
} from './horizontal-nav-context';
import messages from './horizontal-nav.i18n';
import { PopupMenuHeader, BlueBg, DarkBg } from './horizontal-settings.style';

enum MenuItemIds {
  WorkspaceSettings = 'workspace-settings-horizontal-link',
}

const HorizontalSettings = injectIntl(
  ({ intl }: { intl: InjectedIntl }): JSX.Element => {
    const { formatMessage } = intl;
    const {
      currentWorkspace,
      isOrgAdmin,
      workspaceOrgId,
      manageAccountUrl,
      userEmail,
      adminHubUrl,
      loginUrl,
      menuItems,
      publishUiEvent,
    } = useContext<HorizontalNavProps>(HorizontalNavContext);

    const enableClientSideLinking = FeatureGates.checkGate(
      StatsigFeatureKeys.enableClientSideLinking
    );

    const workspaceUuid = currentWorkspace?.uuid;

    const workspaceSettingsItem = menuItems.settingsItems?.find(
      (item: MenuItem) => item.id === MenuItemIds.WorkspaceSettings
    );

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { push } = useRouterActions();

    const onToggle = useCallback(() => {
      setIsOpen(!isOpen);

      publishUiEvent({
        action: 'clicked',
        actionSubject: 'navigationItem',
        actionSubjectId: 'settings',
        source: 'navigation',
      });
    }, [setIsOpen, isOpen, publishUiEvent]);

    const fireEvent = useCallback(
      (settingsType: 'workspace' | 'aid-account' | 'bbc-account' | 'labs') => {
        publishUiEvent({
          action: 'clicked',
          actionSubject: 'link',
          actionSubjectId: 'globalNavSettingsMenu',
          source: 'navigation',
          attributes: {
            settingsType,
          },
        });
      },
      [publishUiEvent]
    );

    const onClose = useCallback(() => {
      setIsOpen(false);
    }, [setIsOpen]);

    const SettingsMenu = useMemo(() => {
      return () => (
        <PopupMenuGroup data-testid="settings-popup-content" maxWidth={500}>
          <PopupMenuHeader>{formatMessage(messages.settings)}</PopupMenuHeader>
          {workspaceUuid && isOrgAdmin && workspaceOrgId && adminHubUrl && (
            <Section title={formatMessage(messages.atlassianAdmin)}>
              <LinkItem
                rel="noopener noreferrer"
                iconBefore={
                  <DarkBg>
                    <PeopleGroupIcon
                      label=""
                      size="small"
                      primaryColor={token('color.icon.inverse')}
                    />
                  </DarkBg>
                }
                description={formatMessage(messages.userManagementDesc)}
                href={urls.ui.adminHub(
                  adminHubUrl,
                  workspaceOrgId,
                  workspaceUuid,
                  'users'
                )}
              >
                {formatMessage(messages.userManagementTitle)}
              </LinkItem>
            </Section>
          )}
          {workspaceSettingsItem && (
            <Section title={formatMessage(messages.bitbucketAdministration)}>
              <LinkItem
                onClick={e => {
                  fireEvent('workspace');
                  if (enableClientSideLinking && !(e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    push(workspaceSettingsItem.url);
                  }
                }}
                iconBefore={
                  <BlueBg>
                    <ActivityIcon
                      label=""
                      size="small"
                      primaryColor={token('color.icon.inverse')}
                    />
                  </BlueBg>
                }
                description={formatMessage(messages.workspaceSettingsDesc)}
                href={workspaceSettingsItem.url}
              >
                {formatMessage(messages.workspaceSettingsTitle)}
              </LinkItem>
            </Section>
          )}
          <Section title={formatMessage(messages.personalSettings)}>
            {/* Atlassian settings menu item */}
            {manageAccountUrl && loginUrl && userEmail && (
              <LinkItem
                onClick={() => fireEvent('aid-account')}
                iconBefore={
                  <BlueBg>
                    <AtlassianIcon
                      label=""
                      size="small"
                      primaryColor={token('color.icon.inverse')}
                    />
                  </BlueBg>
                }
                iconAfter={<ShortcutIcon label="" size="small" />}
                description={formatMessage(messages.atlAcctSettingsDesc)}
                href={urls.external.atlassianAccountSync(
                  loginUrl,
                  manageAccountUrl,
                  userEmail
                )}
                target="_blank"
              >
                {formatMessage(messages.atlAcctSettingsTitle)}
              </LinkItem>
            )}

            {/* Bitbucket settings menu item */}
            <LinkItem
              onClick={e => {
                fireEvent('bbc-account');
                if (enableClientSideLinking && !(e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  push(urls.ui.settings());
                }
              }}
              iconBefore={
                <BlueBg>
                  <PersonCircleIcon
                    label=""
                    size="small"
                    primaryColor={token('color.icon.inverse')}
                  />
                </BlueBg>
              }
              href={urls.ui.settings()}
              description={formatMessage(messages.bbAcctSettingsDesc)}
            >
              {formatMessage(messages.bbAcctSettingsTitle)}
            </LinkItem>

            {/* Labs menu item */}
            <LinkItem
              onClick={e => {
                fireEvent('labs');
                if (enableClientSideLinking && !(e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  push(urls.ui.labs());
                }
              }}
              iconBefore={
                <BlueBg>
                  <LabsIcon
                    label=""
                    size="small"
                    primaryColor={token('color.icon.inverse')}
                  />
                </BlueBg>
              }
              href={urls.ui.labs()}
              description={formatMessage(messages.labsDesc)}
            >
              {formatMessage(messages.labsTitle)}
            </LinkItem>
          </Section>
        </PopupMenuGroup>
      );
    }, [
      loginUrl,
      manageAccountUrl,
      userEmail,
      formatMessage,
      workspaceSettingsItem,
      workspaceUuid,
      adminHubUrl,
      isOrgAdmin,
      workspaceOrgId,
      fireEvent,
      push,
      enableClientSideLinking,
    ]);

    const SettingsCog = useMemo(() => {
      return (tProps: any) => (
        <Settings
          {...tProps}
          tooltip={formatMessage(messages.settings)}
          onClick={onToggle}
          testId="h-nav-settings-cog"
          isSelected={isOpen}
        />
      );
    }, [onToggle, isOpen, formatMessage]);

    return (
      <Popup
        placement="bottom-end"
        content={SettingsMenu}
        isOpen={isOpen}
        onClose={onClose}
        trigger={SettingsCog}
      />
    );
  }
);

export default HorizontalSettings;
